import React from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import PageSeo from "../components/site/PageSeo";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { ArrowRight, Linkedin, Instagram, Music2 } from "lucide-react";
import {
  SITE_URL,
  CYRUS_ID,
  ORG_ID,
  PERSON,
  ORGANIZATION,
  CYRUS_KNOWS_ABOUT,
  SOCIAL_PROFILES,
} from "../data/entityGraph";
import { trackEvent } from "../lib/track";

const PORTRAIT_SRC = "/assets/brand/cyrus-portrait.webp";

const FACETS = [
  {
    label: "Author",
    body: "Wrote Authentic Momentum: a field guide for moving from force into flow.",
  },
  {
    label: "AI Strategist",
    body: "Designs human-first AI systems that turn clarity into compounding momentum.",
  },
  {
    label: "Systems Builder",
    body: "Builds the rails behind founder, creator, and operator brands.",
  },
  {
    label: "Movement Architect",
    body: "Created the Authentic Momentum framework. Signal, frame, action.",
  },
];

const KNOWN_FOR = [
  "Creator of the Authentic Momentum framework.",
  "Strategist behind brand systems for founders and creators.",
  "Builder of AI-assisted clarity tools (Clarity Snapshot, Personal Action Plan, Clarity Reset Session).",
  "Featured in The Smile Channel.",
];

const profileLd = {
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${SITE_URL}/cyrus-prescod#profile`,
  url: `${SITE_URL}/cyrus-prescod`,
  mainEntity: { ...PERSON },
  about: { ...PERSON },
  inLanguage: "en",
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Cyrus Prescod", item: `${SITE_URL}/cyrus-prescod` },
  ],
};

const SocialLink = ({ profile, testid }) => {
  if (!profile.active || !profile.url) return null;
  const Icon = profile.label === "LinkedIn" ? Linkedin : profile.label === "Instagram" ? Instagram : Music2;
  return (
    <a
      href={profile.url}
      target="_blank"
      rel="noopener me noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 hover:border-gold/70 hover:text-gold text-themed-muted transition-colors text-[12px] tracking-[0.22em] uppercase rounded-full"
    >
      <Icon size={13} strokeWidth={1.4} />
      <span>{profile.label}</span>
    </a>
  );
};

const CyrusProfilePage = () => (
  <>
    <PageSeo
      title="Cyrus Prescod | Founder of Authentic Momentum, AI Strategist, Systems Builder"
      description="Cyrus Prescod is the founder of Authentic Momentum, an AI strategist and systems builder helping founders, creators, and operators move from force into flow."
      canonical={`${SITE_URL}/cyrus-prescod`}
      ogType="profile"
      ogImage={`${SITE_URL}/assets/brand/cyrus-portrait.webp`}
      jsonLd={[profileLd, breadcrumbLd, { "@context": "https://schema.org", ...ORGANIZATION }]}
    />
    <Navigation />
    <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />

      <section className="relative max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative w-full max-w-sm mx-auto">
            <div
              className="absolute -inset-6 rounded-[28px] blur-2xl opacity-60 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }}
              aria-hidden
            />
            <div className="relative rounded-[22px] p-[1.5px] bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#7A5F12]">
              <div className="rounded-[20px] overflow-hidden bg-black/40">
                <img
                  src={PORTRAIT_SRC}
                  alt="Cyrus Prescod, founder of Authentic Momentum"
                  data-testid="cyrus-profile-portrait"
                  className="block w-full aspect-[4/5] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <p className="mt-5 text-center text-[10px] tracking-[0.4em] uppercase text-gold/80">
              Cyrus Prescod · Founder
            </p>
          </div>
        </Reveal>

        <Reveal delay={140} className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
            Founder Profile
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-themed leading-tight tracking-tight font-light">
            Cyrus{" "}
            <span className="italic gold-gradient-text">Prescod.</span>
          </h1>
          <p className="mt-7 text-white/85 leading-relaxed max-w-2xl text-[17px]">
            Founder of Authentic Momentum. AI strategist, systems builder, and author of a framework that helps people move from force into flow.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <SocialLink profile={SOCIAL_PROFILES.linkedin} testid="cyrus-social-linkedin" />
            <SocialLink profile={SOCIAL_PROFILES.instagram} testid="cyrus-social-instagram" />
            <SocialLink profile={SOCIAL_PROFILES.tiktok} testid="cyrus-social-tiktok" />
            <a
              href="/connect"
              data-testid="cyrus-all-links"
              className="inline-flex items-center gap-2 px-4 py-2 text-themed-muted hover:text-gold transition-colors text-[12px] tracking-[0.22em] uppercase"
            >
              All Links <ArrowRight size={13} />
            </a>
          </div>
        </Reveal>
      </section>

      <section
        data-testid="cyrus-facets"
        className="relative max-w-6xl mx-auto px-6 md:px-10 mt-24 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6"
      >
        {FACETS.map((f, i) => (
          <Reveal key={f.label} delay={120 + i * 80}>
            <div className="bg-white/5 border-t border-gold/20 p-7 md:p-8 rounded-lg border border-gold/10 h-full">
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">{f.label}</p>
              <p className="mt-4 text-white/85 text-[15.5px] leading-relaxed">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-24">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">Known For</p>
          <ul data-testid="cyrus-known-for" className="space-y-3 max-w-2xl mx-auto">
            {KNOWN_FOR.map((line, i) => (
              <li key={i} className="flex gap-4 text-white/85 leading-relaxed">
                <span className="font-serif text-gold text-xl leading-none">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1">{line}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24">
        <Reveal>
          <div className="bg-white/5 border-t border-gold/20 p-8 md:p-10 rounded-lg border border-gold/10">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">Areas of Focus</p>
            <div className="flex flex-wrap gap-2" data-testid="cyrus-knows-about">
              {CYRUS_KNOWS_ABOUT.map((topic) => (
                <span
                  key={topic}
                  className="text-[12px] tracking-[0.16em] uppercase text-gold/85 border border-gold/25 px-3 py-1.5 rounded-full"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-24">
        <Reveal>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="/framework"
              data-testid="cyrus-cta-framework"
              onClick={() => trackEvent("cyrus_profile_to_framework_clicked")}
              className="btn-ghost-gold inline-flex"
            >
              Read the Framework
            </a>
            <a
              href="/about"
              data-testid="cyrus-cta-about"
              onClick={() => trackEvent("cyrus_profile_to_about_clicked")}
              className="btn-ghost-gold inline-flex"
            >
              The Story Behind the Movement
            </a>
            <a
              href="/press"
              data-testid="cyrus-cta-press"
              onClick={() => trackEvent("cyrus_profile_to_press_clicked")}
              className="btn-ghost-gold inline-flex"
            >
              Press and Validation
            </a>
          </div>
        </Reveal>
      </section>
    </main>
    <Footer />
    <OrientationResetCheckoutHost />
  </>
);

export default CyrusProfilePage;
